import StateObserver from 'src/StateObserver';
import {
  GCInstant,
  getFriendshipAsync,
  isLiffFriendshipApiSupported,
  requestFriendshipAsync,
} from 'src/lib/gcinstant';
import { isPlatformGuest, withLoading } from 'src/lib/utils';
import { isPlatformNative } from 'src/lib/native/native.util';
import { captureGenericError } from 'src/lib/sentry';
import { hasGrantedProfilePermission } from '../profile/profile.api';
import {
  trackOfficialAccountFriendClose,
  trackOfficialAccountFriendOpen,
  type ConsentSurface,
} from '../launch/onboarding.analytics';
import { trackEntryFirstSubscribe } from '../launch/entryFirstSubscribe.analytics';
import type { IncentivizedOARewardClaimResult } from 'src/replicant/chatbot/chatbot.ruleset';

/**
 * Reusable LINE Official Account (OA) / chatbot operations: requesting the
 * Add-Friend overlay (with its open/close analytics) and detecting subscription.
 * A thin, domain-named wrapper over the LIFF primitives in `src/lib/gcinstant.ts`
 * plus the realtime subscription async getter. No AB/bucket logic lives here.
 */

/**
 * Whether LINE's native Add-Friend overlay CAN be shown: a non-guest, non-native
 * (LIFF/web) user on a client whose LIFF supports the friendship API. Pure
 * capability — callers add the business rule (e.g. not already subscribed, via
 * `GCInstant.isSubscribedToBot`). Native is excluded (no LIFF API to request),
 * though a native user can still be detected as subscribed by
 * {@link isSubscribedToOfficialAccount}.
 */
export function canRequestOfficialAccountFriend(): boolean {
  return (
    !isPlatformGuest(GCInstant) &&
    !isPlatformNative() &&
    isLiffFriendshipApiSupported()
  );
}

/**
 * Check whether a LINE SDK error represents a user-cancelled native prompt.
 *
 * @param error Error returned by the LINE friendship request.
 * @returns Whether the user dismissed or cancelled the native prompt.
 */
function isUserInputError(error: unknown): boolean {
  // LINE identifies user-cancelled native prompts with the USER_INPUT code.
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    error.code === 'USER_INPUT'
  );
}

/**
 * SERVER-side OA subscription status, the authoritative source and the only one
 * that works for native (no LIFF). A single async getter round-trip: the server
 * checks the cached last-known status first and only falls back to the realtime
 * LINE check (rate-limited) when the cache says not-subscribed. Fails closed
 * (`false`), reporting an unexpected failure to Sentry. Never rejects.
 */
async function checkSubscriptionFromServer(): Promise<boolean> {
  return StateObserver.replicant.asyncGetters
    .isSubscribedToOfficialAccount()
    .catch((error) => {
      captureGenericError('OA subscription server check failed', error);
      return false;
    });
}

/**
 * CLIENT-side OA friendship read via the LIFF SDK (`getFriendshipAsync`). Call
 * ONLY when {@link isLiffFriendshipApiSupported} (platform/LIFF correctness) and
 * the `profile` scope is granted, so it resolves silently instead of popping the
 * consent popover. An unexpected SDK failure is reported and falls back to the
 * authoritative server check rather than guessing.
 */
async function checkFriendshipFromClient(): Promise<boolean> {
  try {
    return await getFriendshipAsync();
  } catch (error) {
    captureGenericError('OA friendship client check failed', error);
    return checkSubscriptionFromServer();
  }
}

/**
 * Authoritative OA subscription status. The client read is used only on a LIFF
 * client with the friendship API (platform/LIFF correctness) AND once the
 * `profile` scope is granted — there it is side-effect-free, so it's preferred;
 * everything else uses the authoritative server round-trip. Pass
 * `{ forceServerSide: true }` for arms that must NEVER risk popping the consent
 * popover (the Add-Friend-only arm). Fails closed (`false`) for a guest and never
 * rejects.
 */
export async function isSubscribedToOfficialAccount(opts?: {
  forceServerSide?: boolean;
}): Promise<boolean> {
  if (isPlatformGuest(GCInstant)) return false;

  // Add-Friend-only arms force the server path; the client read also needs LIFF
  // friendship support before we even query the scope.
  if (!opts?.forceServerSide && isLiffFriendshipApiSupported()) {
    const profileGranted = await hasGrantedProfilePermission();
    if (profileGranted) return checkFriendshipFromClient();
  }

  return checkSubscriptionFromServer();
}

/**
 * Show LINE's Add-Friend overlay ONCE, emit the bracketing open/close analytics,
 * and report whether the user is subscribed afterwards — wrapped in a loading
 * spinner. Self-guards on {@link canRequestOfficialAccountFriend} (non-guest,
 * non-native, LIFF friendship API): when the overlay can't be shown it returns
 * `false` immediately, before any analytics. Verification uses
 * {@link isSubscribedToOfficialAccount}; pass `{ forceServerSide: true }` for the
 * Add-Friend-only arm so the verification can't trigger the consent popover.
 *
 * Never throws: a LIFF/SDK failure resolves to `false` (reported to Sentry) with
 * the Open/Close analytics still balanced, so a failed attempt can never abort
 * launch even at the splash, outside the launch sequence's error boundary.
 */
export async function requestOfficialAccountFriend(
  origin: ConsentSurface,
  opts?: { forceServerSide?: boolean },
): Promise<boolean> {
  if (!canRequestOfficialAccountFriend()) return false;

  const allTimeViewCount = await trackOfficialAccountFriendOpen(origin);

  let subscribed = false;
  try {
    subscribed = await withLoading(async () => {
      await requestFriendshipAsync();
      return isSubscribedToOfficialAccount(opts);
    });
  } catch (error) {
    captureGenericError('requestOfficialAccountFriend failed', error);
  } finally {
    trackOfficialAccountFriendClose(origin, subscribed, allTimeViewCount);
    if (subscribed) {
      trackEntryFirstSubscribe('addFriend', origin);
    }
  }

  return subscribed;
}

/**
 * Request LINE OA friendship, verify it, and claim A/B 0489.
 *
 * Production verifies through the server so the flow cannot request profile
 * consent. Development retries through LIFF because the friendship request and
 * backend subscription check currently target different Official Accounts.
 *
 * This deliberately provides no game loading UI. The calling surface owns its
 * bubble, spinner, or persistent popup so it remains visible under LINE's
 * native half-modal.
 *
 * @returns The verified reward claim outcome.
 */
export async function requestAndClaimIncentivizedOAAddition(): Promise<IncentivizedOARewardClaimResult> {
  // Unsupported clients cannot render the LINE Add-Friend half-modal.
  if (!canRequestOfficialAccountFriend()) return 'ineligible';

  try {
    // Ask LINE to add or unblock the Official Account.
    await requestFriendshipAsync();

    // Prefer server verification so production never requests profile consent.
    let isSubscribed = await isSubscribedToOfficialAccount({
      forceServerSide: true,
    });

    // In development, the friendship request targets the production OA while
    // the backend checks the development OA. Retry through LIFF so local tests
    // can observe the friendship state changed by the request above.
    if (!isSubscribed && process.env.IS_DEVELOPMENT) {
      isSubscribed = await isSubscribedToOfficialAccount();
    }

    if (!isSubscribed) return 'not_subscribed';

    // Grant the lifetime reward atomically after subscription is verified.
    return await StateObserver.invoke.claimIncentivizedOAAdditionReward();
  } catch (error) {
    // User dismissal is an expected outcome and should not be reported.
    if (!isUserInputError(error)) {
      captureGenericError(
        'requestAndClaimIncentivizedOAAddition failed',
        error,
      );
    }

    // The reward remains available for a later attempt.
    return 'not_subscribed';
  }
}
